import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../components/ui/PageHeader';
import { Card, CardContent, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { CreateIndustryProfileForm } from '../components/industryProfiles/CreateIndustryProfileForm';
import { IndustryProfileVersionDetail, statusLabel } from '../components/industryProfiles/IndustryProfileVersionDetail';
import {
  addIndustryProfileMapping,
  createIndustryProfileDraft,
  deleteIndustryProfileMapping,
  loadIndustryProfileAdminData,
  publishIndustryProfileVersion,
  retireIndustryProfileVersion,
  updateIndustryProfileMapping,
  verifyIndustryProfileAdminAccess
} from '../services/industryProfileAdminService';

const dateLabel = (value) => value
  ? new Intl.DateTimeFormat('nb-NO', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
  : '—';

export const IndustryProfiles = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [access, setAccess] = useState('checking');
  const [data, setData] = useState({ profiles: [], versions: [], catalogs: { hazards: [], factors: [], questions: [] } });
  const selectedVersionId = searchParams.get('version') || '';
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [publishConfirmation, setPublishConfirmation] = useState(false);

  const selectVersion = (versionId) => {
    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      if (versionId) {
        nextParams.set('version', versionId);
      } else {
        nextParams.delete('version');
      }
      return nextParams;
    }, { replace: true });
  };

  const refresh = async () => setData(await loadIndustryProfileAdminData());

  useEffect(() => {
    let active = true;
    verifyIndustryProfileAdminAccess()
      .then(async (allowed) => {
        if (!active) return;
        if (!allowed) {
          setAccess('denied');
          return;
        }
        await refresh();
        if (active) setAccess('allowed');
      })
      .catch((failure) => {
        if (active) {
          setError(failure.message || 'Tilgangskontrollen mislyktes.');
          setAccess('error');
        }
      });
    return () => { active = false; };
  }, []);

  const selectedVersion = useMemo(
    () => data.versions.find((version) => version.id === selectedVersionId),
    [data.versions, selectedVersionId]
  );

  const run = async (operation) => {
    setBusy(true);
    setError('');
    try {
      await operation();
      await refresh();
    } catch (failure) {
      setError(failure.message || 'Handlingen kunne ikke fullføres.');
    } finally {
      setBusy(false);
    }
  };

  const createDraft = (values) => run(async () => {
    const created = await createIndustryProfileDraft(values);
    setShowCreate(false);
    await refresh();
    selectVersion(created.created_profile_version_id);
  });

  const mutateMapping = (operation, type, ...args) => run(async () => {
    if (operation === 'add') {
      await addIndustryProfileMapping(type, selectedVersionId, args[0], args[1]);
    } else if (operation === 'update') {
      await updateIndustryProfileMapping(type, args[0], args[1]);
    } else if (operation === 'delete') {
      if (!window.confirm('Vil du fjerne denne koblingen fra utkastet?')) return;
      await deleteIndustryProfileMapping(type, args[0]);
    }
  });

  const lifecycle = (action, confirmed = false) => {
    const isPublish = action === 'publish';
    const prompt = isPublish
      ? 'Publisere denne profilversjonen? Etter publisering kan koblingene ikke redigeres.'
      : 'Markere denne publiserte profilversjonen som utgått?';
    if (isPublish && !confirmed) {
      setPublishConfirmation(true);
      return;
    }
    if (!isPublish && !window.confirm(prompt)) return;
    run(() => isPublish
      ? publishIndustryProfileVersion(selectedVersionId)
      : retireIndustryProfileVersion(selectedVersionId));
  };

  const confirmPublish = () => {
    setPublishConfirmation(false);
    lifecycle('publish', true);
  };

  if (access === 'checking') return <InfoPanel title="Kontrollerer tilgang" text="Verifiserer innlogget bruker og SUPERADMIN-tilgang …" />;
  if (access === 'denied') return <InfoPanel title="Ingen tilgang" text="Denne interne siden krever en innlogget SUPERADMIN-bruker." />;
  if (access === 'error') return <InfoPanel title="Tilgangskontroll feilet" text={error || 'SUPERADMIN-tilgangen kunne ikke verifiseres.'} />;

  return (
    <div className="space-y-8">
      <PageHeader
        title="Bransjeprofiler"
        intro="Administrer profiler, versjoner og faglige koblinger."
        breadcrumb={<><span>System & Admin</span><span>/</span><span>Bransjeprofiler</span></>}
        actions={<Button onClick={() => setShowCreate((visible) => !visible)}>{showCreate ? 'Lukk skjema' : 'Opprett bransjeprofil'}</Button>}
      />

      {error && <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">{error}</div>}

      {showCreate && (
        <Card>
          <CardHeader title="Opprett bransjeprofil" subtitle="Oppretter profil og første versjon som utkast." />
          <CardContent>
            <CreateIndustryProfileForm busy={busy} onCancel={() => setShowCreate(false)} onSubmit={createDraft} />
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(420px,1.15fr)]">
        <Card>
          <CardHeader title="Profiler og versjoner" subtitle={`${data.profiles.length} profiler`} action={<Button variant="secondary" size="sm" disabled={busy} onClick={() => run(refresh)}>Oppdater</Button>} />
          <CardContent className="space-y-5">
            {data.profiles.length === 0 && <p className="rounded-xl bg-slate-50 p-5 text-sm text-slate-500">Ingen bransjeprofiler ennå.</p>}
            {data.profiles.map((profile) => {
              const versions = data.versions.filter((version) => version.profile_id === profile.id);
              return (
                <section key={profile.id} className="overflow-hidden rounded-xl border border-slate-200">
                  <div className="bg-slate-50 px-4 py-3">
                    <p className="font-bold text-slate-900">{profile.profile_key}</p>
                    <p className="mt-1 text-xs text-slate-500">Opprettet {dateLabel(profile.created_at)}</p>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {versions.map((version) => (
                      <button
                        key={version.id}
                        type="button"
                        onClick={() => selectVersion(version.id)}
                        className={`w-full p-4 text-left transition-colors ${selectedVersionId === version.id ? 'bg-blue-50/70' : 'hover:bg-slate-50'}`}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-semibold text-slate-800">{version.display_name} · v{version.version_number}</span>
                          <StatusBadge status={version.status} />
                        </div>
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                          <span>Opprettet {dateLabel(version.created_at)}</span>
                          {version.published_at && <span>Publisert {dateLabel(version.published_at)}</span>}
                          {version.retired_at && <span>Utgått {dateLabel(version.retired_at)}</span>}
                        </div>
                      </button>
                    ))}
                    {versions.length === 0 && <p className="p-4 text-sm text-slate-500">Ingen versjoner registrert.</p>}
                  </div>
                </section>
              );
            })}
          </CardContent>
        </Card>

        <div className="min-w-0">
          {selectedVersion ? (
            <IndustryProfileVersionDetail
              version={selectedVersion}
              profileKey={data.profiles.find((profile) => profile.id === selectedVersion.profile_id)?.profile_key || ''}
              catalogs={data.catalogs}
              busy={busy}
              onMutation={mutateMapping}
              onPublish={() => lifecycle('publish')}
              onRetire={() => lifecycle('retire')}
            />
          ) : (
            <Card>
              <CardContent className="py-14 text-center">
                <p className="font-semibold text-slate-800">Velg en profilversjon</p>
                <p className="mt-2 text-sm text-slate-500">Detaljer og koblinger vises her.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {publishConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
          <section
            aria-labelledby="publish-confirmation-title"
            aria-modal="true"
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
            role="alertdialog"
          >
            <h2 id="publish-confirmation-title" className="text-lg font-semibold text-slate-900">Publisere profilversjonen?</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">Etter publisering kan koblingene ikke redigeres.</p>
            <div className="mt-6 flex justify-end gap-3">
              <Button type="button" variant="secondary" disabled={busy} onClick={() => setPublishConfirmation(false)}>Avbryt</Button>
              <Button type="button" disabled={busy} onClick={confirmPublish}>Bekreft publisering</Button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

const InfoPanel = ({ title, text }) => (
  <Card>
    <CardContent className="py-14 text-center">
      <h1 className="text-xl font-bold text-slate-900">{title}</h1>
      <p className="mt-2 text-sm text-slate-500">{text}</p>
    </CardContent>
  </Card>
);

const StatusBadge = ({ status }) => {
  const classes = {
    draft: 'bg-amber-100 text-amber-800',
    published: 'bg-emerald-100 text-emerald-800',
    retired: 'bg-slate-200 text-slate-700'
  };
  return <span className={`rounded-full px-3 py-1 text-xs font-bold ${classes[status] || 'bg-slate-100 text-slate-700'}`}>{statusLabel(status)}</span>;
};